import { isAxiosError } from 'axios';

/**
 * Client errors that never succeed on an immediate retry. Retrying them just
 * amplifies load — and for 429 (per-org rate limit, see the backend
 * `auth_middleware` token bucket) a blind retry storm is exactly what turns a
 * brief cold-load burst into "many red requests" in the console. Treat every
 * 4xx as terminal; only transient failures (5xx / network / timeout, which have
 * no `response.status`) are worth retrying.
 */
const NON_RETRYABLE_STATUSES = new Set([400, 401, 403, 404, 405, 406, 409, 422, 429]);

/** TanStack Query `retry` predicate: retry transient failures up to `max`, but
 *  never a deterministic client (4xx) error. */
export function retryUnlessClientError(failureCount: number, error: unknown): boolean {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  if (status !== undefined && NON_RETRYABLE_STATUSES.has(status)) {
    return false;
  }
  return failureCount < 2;
}

/**
 * Statuses worth retrying on a WRITE. Deliberately much narrower than the read
 * policy above, because a mutation is not idempotent: 502/503/504 all come from
 * a proxy in front of the service and mean the request never reached its target,
 * so replaying it cannot double-apply anything. A 500 is excluded on purpose —
 * the handler ran and may have committed before failing, and silently replaying
 * that could create the same resource twice.
 *
 * This exists because every write in the app ran with TanStack Query's default
 * `retry: 0` while every read retried transient failures twice. A single blip in
 * a dependency was therefore invisible on a GET and fatal on a POST — which is
 * how a ~50 s compiler outage turned into a user manually re-clicking "create
 * study" until it worked.
 */
const RETRYABLE_MUTATION_STATUSES = new Set([502, 503, 504]);

/**
 * `retry` for a BILLED call the server cannot recognise as a replay — a sandbox
 * run, a basket simulation, a data-source preview, a create that charges.
 *
 * Stricter than BOTH default policies, and the difference is one case: a failure
 * carrying NO status. `retryUnlessClientError` (reads) and `retryTransientMutation`
 * (writes) both replay it, on the reasoning that a network error / timeout /
 * aborted connection never landed. That reasoning is about the CLIENT's socket,
 * not the server's work: a request that timed out may have arrived, run, charged
 * and had its answer lost on the way back. Replaying it bills the organization a
 * second time, with nothing to refund because nothing failed.
 *
 * What survives is 502/503/504 — those come from a proxy IN FRONT of the service
 * and mean the request never reached the handler, so replaying cannot double-charge.
 * A 500 is excluded for the same reason `retryTransientMutation` excludes it: the
 * handler ran and may have committed before failing.
 *
 * Use this instead of `retry: false` wherever the call costs tokens: it keeps the
 * resilience that is provably safe and drops only the replay that costs money.
 * A call that DOES carry an idempotency key (`client_request_id`,
 * `study_derivation_batches`) does not need this — the server recognises the
 * repeat, so it can stay on the ordinary policy.
 */
export function retryOnlyIfNeverLanded(failureCount: number, error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  const status = error.response?.status;
  if (status === undefined) return false;
  return RETRYABLE_MUTATION_STATUSES.has(status) && failureCount < 2;
}

/** TanStack Query `retry` predicate for MUTATIONS: retry only the failures that
 *  provably never reached the handler. */
export function retryTransientMutation(failureCount: number, error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  const status = error.response?.status;
  // No status at all = network error / timeout / aborted connection: the request
  // did not complete, so it is in the same "never landed" class as a 502.
  if (status !== undefined && !RETRYABLE_MUTATION_STATUSES.has(status)) {
    return false;
  }
  return failureCount < 2;
}

/** TanStack Query `retryDelay`: honor a server `Retry-After` header when present
 *  (seconds, capped), otherwise fall back to capped exponential backoff. */
export function retryDelayWithRetryAfter(attemptIndex: number, error: unknown): number {
  const MAX_DELAY_MS = 30_000;
  if (isAxiosError(error)) {
    const raw = error.response?.headers?.['retry-after'];
    const seconds = Array.isArray(raw) ? Number(raw[0]) : Number(raw);
    if (Number.isFinite(seconds) && seconds > 0) {
      return Math.min(seconds * 1000, MAX_DELAY_MS);
    }
  }
  return Math.min(1000 * 2 ** attemptIndex, MAX_DELAY_MS);
}
